import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  ListChecks,
  Lock,
  LockOpen,
  Pencil,
  Scale,
  Trash2,
  Undo2,
} from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Modal } from '@/shared/ui/Modal';
import { Paginador } from '@/shared/ui/Paginador';
import { Pestanas } from '@/shared/ui/Pestanas';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { notificar } from '@/shared/ui/avisos';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { CargaDePesajes } from './CargaDePesajes';
import { RegistroDePesaje } from './RegistroDePesaje';
import {
  enUnidad,
  type Ciclo,
  type Consolidacion,
  type ConsolidadoPorGenerador,
  type Pesaje,
} from './contrato';

export function PaginaControlDePesaje() {
  // El mes desplegado: al entrar, el abierto; `null` es que el usuario los plego todos.
  const [expandido, setExpandido] = useState<number | null | undefined>(undefined);
  const [paginaDeMeses, setPaginaDeMeses] = useState(0);
  const [vista, setVista] = useState<'pesajes' | 'consolidado'>('pesajes');
  const [texto, setTexto] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [abriendo, setAbriendo] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [cargandoExcel, setCargandoExcel] = useState(false);
  const [corrigiendo, setCorrigiendo] = useState<Pesaje | null>(null);
  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();
  const puedeGestionar = puede('generadores.gestionar');

  const ciclos = useQuery({
    queryKey: ['generadores', 'ciclos'],
    queryFn: () => api.get<Ciclo[]>('/generadores/ciclos'),
  });

  const cicloAbierto = ciclos.data?.find((c) => c.estado === 'ABIERTO');
  const ciclo =
    expandido === undefined
      ? cicloAbierto
      : expandido === null
        ? undefined
        : ciclos.data?.find((c) => c.id === expandido);
  const hayAbierto = cicloAbierto !== undefined;

  const todos = ciclos.data ?? [];
  const visibles = todos.slice(
    paginaDeMeses * MESES_POR_PAGINA,
    (paginaDeMeses + 1) * MESES_POR_PAGINA,
  );
  // La barra de cada mes se mide contra el mes que mas facturo.
  const mayorTotal = Math.max(1, ...todos.map((c) => c.total));
  const delAnio = (anio: string) => todos.filter((c) => c.periodo.startsWith(anio));

  function alternar(id: number) {
    setExpandido(ciclo?.id === id ? null : id);
    setVista('pesajes');
    setTexto('');
    setPagina(0);
  }

  const buscado = useValorDiferido(texto.trim());
  const pesajes = useQuery({
    queryKey: ['generadores', 'pesajes', ciclo?.id, buscado, pagina, tamanio],
    enabled: ciclo !== undefined,
    queryFn: () =>
      api.get<Pagina<Pesaje>>(`/generadores/ciclos/${ciclo!.id}/pesajes`, {
        query: { texto: buscado, pagina, tamanio },
      }),
    placeholderData: (anterior) => anterior,
  });
  const consolidado = useQuery({
    queryKey: ['generadores', 'consolidado', ciclo?.id],
    enabled: ciclo !== undefined,
    queryFn: () => api.get<Consolidacion>(`/generadores/ciclos/${ciclo!.id}/consolidado`),
  });

  function refrescar() {
    void clienteDeConsultas.invalidateQueries({ queryKey: ['generadores'] });
  }

  const eliminar = useMutation({
    mutationFn: (p: Pesaje) => api.delete(`/generadores/pesajes/${p.id}`),
    onSuccess: () => {
      notificar.exito('Pesaje eliminado');
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO ELIMINAR'),
  });

  const reabrir = useMutation({
    mutationFn: () => api.post<Ciclo>(`/generadores/ciclos/${ciclo!.id}/reabrir`),
    onSuccess: (c) => {
      notificar.exito(c.periodo, 'Ciclo reabierto');
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO REABRIR'),
  });

  const abierto = ciclo?.estado === 'ABIERTO';
  const facturado = ciclo?.estado === 'FACTURADO';
  // Un pesaje se toca con el ciclo abierto, o despues si una nota de credito lo solto.
  const tocable = (p: Pesaje) => abierto || (facturado && !p.facturado);
  const t = consolidado.data?.totales;

  const columnas: Columna<Pesaje>[] = [
    {
      clave: 'fecha',
      encabezado: 'Fecha',
      contenido: (p) => (
        <span className="importe text-xs text-armazon-600">{enFecha(p.fecha)}</span>
      ),
      pie: <span className="uppercase">Total del ciclo</span>,
    },
    {
      clave: 'generador',
      encabezado: 'Generador',
      contenido: (p) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-medium uppercase text-armazon-800">
            {p.generador}
          </span>
          <span className="importe block text-[10px] text-armazon-400">{p.identificacion}</span>
          {p.facturaAnterior && (
            <span
              title="Pesaje del historial: se facturó fuera de SIREPAGAL y no se toca"
              className="mt-0.5 inline-flex items-center rounded bg-armazon-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-armazon-600"
            >
              {p.facturaAnterior === 'SIRE'
                ? 'Facturado en el SIRE'
                : `Sistema anterior · ${p.facturaAnterior}`}
            </span>
          )}
          {facturado && !p.facturado && (
            <span
              title="Su factura quedó sin efecto por una nota de crédito o una anulación"
              className="mt-0.5 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-800"
            >
              <Undo2 className="size-3" aria-hidden />
              Liberado · por refacturar
            </span>
          )}
        </span>
      ),
      pie: t && (
        <span className="text-[11px] font-semibold uppercase text-armazon-500">
          {t.pesajes} pesajes · {t.generadores} generadores
        </span>
      ),
    },
    {
      clave: 'categoria',
      encabezado: 'Cat.',
      alinear: 'centro',
      contenido: (p) => (
        <span
          title={p.categoria}
          className="grid size-5 place-items-center rounded bg-marca-100 text-[10px] font-bold uppercase text-marca-800"
        >
          {p.literal}
        </span>
      ),
    },
    {
      clave: 'peso',
      encabezado: 'Peso',
      alinear: 'derecha',
      contenido: (p) => (
        <span className="importe text-xs text-armazon-700">
          {p.kilos.toLocaleString('es-EC')} KG
        </span>
      ),
      pie: t && (
        <span className="importe text-xs font-bold">{t.kilos.toLocaleString('es-EC')} KG</span>
      ),
    },
    {
      clave: 'guia',
      encabezado: 'Guía',
      contenido: (p) => (
        <span className="importe text-[11px] text-armazon-500">{p.guia ?? '—'}</span>
      ),
    },
    {
      clave: 'tarifa',
      encabezado: 'Tarifa',
      alinear: 'derecha',
      contenido: (p) => (
        <span className="importe text-[11px] text-armazon-500">
          ${p.tarifaUnitaria.toFixed(2)}/{enUnidad(p.unidad)}
        </span>
      ),
    },
    {
      clave: 'recargo',
      encabezado: 'Recargo',
      alinear: 'derecha',
      contenido: (p) =>
        p.recargo > 0 ? (
          <span
            className="importe text-[11px] text-amber-700"
            title={`${p.kmExcedentes} km en exceso`}
          >
            ${p.recargo.toFixed(2)}
          </span>
        ) : (
          <span className="text-[11px] text-armazon-300">—</span>
        ),
      pie: t && <span className="importe text-xs font-bold">${t.recargo.toFixed(2)}</span>,
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (p) => (
        <span className="importe text-xs font-bold text-armazon-900">${p.total.toFixed(2)}</span>
      ),
      pie: t && <span className="importe text-sm font-bold">${t.total.toFixed(2)}</span>,
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      contenido: (p) =>
        tocable(p) &&
        puedeGestionar && (
          <span className="inline-flex gap-1">
            <BotonDeFila
              icono={Pencil}
              titulo="Corregir este pesaje"
              alPulsar={() => setCorrigiendo(p)}
            />
            <BotonDeFila
              icono={Trash2}
              titulo="Quitar este pesaje"
              alPulsar={() => eliminar.mutate(p)}
              ocupado={eliminar.isPending && eliminar.variables?.id === p.id}
            />
          </span>
        ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={Scale}
        titulo="Control de pesaje"
        subtitulo="El mes de pesajes de los generadores: se abre, se registra y se cierra"
        acciones={
          puedeGestionar &&
          !hayAbierto && (
            <button type="button" onClick={() => setAbriendo(true)} className={BOTON_SECUNDARIO}>
              <CalendarPlus className="size-4" aria-hidden />
              Abrir ciclo
            </button>
          )
        }
      />

      {ciclos.data && ciclos.data.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-armazon-300 bg-white p-12 text-center">
          <Scale className="mx-auto size-10 text-armazon-200" aria-hidden />
          <p className="mt-3 text-sm font-semibold uppercase text-armazon-600">
            Todavía no hay ningún ciclo
          </p>
          <p className="mt-1 text-xs uppercase text-armazon-400">
            Abra el del mes que va a registrar para empezar a cargar pesajes
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Resumen rotulo="Meses registrados" valor={String(todos.length)} />
            <Resumen
              rotulo="Pesajes"
              valor={todos.reduce((n, c) => n + c.pesajes, 0).toLocaleString('es-EC')}
            />
            <Resumen
              rotulo="Valor de los pesajes"
              valor={`$${enDolares(todos.reduce((n, c) => n + c.total, 0))}`}
              destacado
            />
          </div>

          <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
            {visibles.map((c, indice) => {
              const desplegado = ciclo?.id === c.id;
              const anio = c.periodo.slice(0, 4);
              const empiezaAnio = indice === 0 || !visibles[indice - 1]!.periodo.startsWith(anio);
              const estilo = ESTILO_DEL_ESTADO[c.estado];
              return (
                <div key={c.id} className="border-b border-armazon-100 last:border-b-0">
                  {empiezaAnio && (
                    <div className="flex items-center justify-between border-b border-armazon-100 bg-armazon-50 px-5 py-2 text-[10px] font-bold uppercase tracking-widest text-armazon-500">
                      <span>{anio}</span>
                      <span className="importe font-semibold normal-case tracking-normal text-armazon-400">
                        {delAnio(anio).length} {delAnio(anio).length === 1 ? 'MES' : 'MESES'} · $
                        {enDolares(delAnio(anio).reduce((n, x) => n + x.total, 0))}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => alternar(c.id)}
                    aria-expanded={desplegado}
                    className={`group flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-armazon-50 ${desplegado ? 'bg-marca-50/50' : ''}`}
                  >
                    <span
                      className={`grid size-12 shrink-0 place-content-center rounded-xl border text-center ${estilo.ficha}`}
                    >
                      <span className="text-sm font-black leading-none">
                        {enMes(c.periodo).slice(0, 3)}
                      </span>
                      <span className="mt-0.5 text-[10px] font-semibold leading-none opacity-70">
                        {anio}
                      </span>
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-bold uppercase text-armazon-900">
                          {enMesLargo(c.periodo)}
                        </span>
                        {c.abiertoPor === 'migracion' && (
                          <span className="rounded bg-armazon-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-armazon-500">
                            Migrado
                          </span>
                        )}
                        {c.estado === 'FACTURADO' && c.sinFacturar > 0 && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-800">
                            {c.sinFacturar} por refacturar
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block text-[11px] uppercase text-armazon-400">
                        {enQueQuedo(c)} · SBU ${enDolares(c.sbu)}
                      </span>
                      <span className="mt-1.5 block h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-armazon-100">
                        <span
                          className={`block h-full rounded-full ${estilo.barra}`}
                          style={{ width: `${Math.round((c.total / mayorTotal) * 100)}%` }}
                        />
                      </span>
                    </span>

                    <span className="hidden w-20 shrink-0 text-right sm:block">
                      <span className="importe block text-sm font-semibold text-armazon-800">
                        {c.pesajes.toLocaleString('es-EC')}
                      </span>
                      <span className="block text-[10px] uppercase text-armazon-400">Pesajes</span>
                    </span>
                    <span className="w-28 shrink-0 text-right">
                      <span className="importe block text-sm font-bold text-armazon-900">
                        ${enDolares(c.total)}
                      </span>
                      <span className="block text-[10px] uppercase text-armazon-400">Total</span>
                    </span>
                    <span className="hidden w-24 shrink-0 justify-end md:flex">
                      <EstadoDelCiclo estado={c.estado} />
                    </span>
                    <ChevronRight
                      className={`size-5 shrink-0 text-armazon-300 transition-transform group-hover:text-armazon-500 ${desplegado ? 'rotate-90 text-marca-600' : ''}`}
                      aria-hidden
                    />
                  </button>

                  {desplegado && ciclo && (
                    <div className="border-t border-armazon-100 bg-armazon-50/40 p-4">
                      {puedeGestionar && (abierto || ciclo.estado === 'CERRADO') && (
                        <div className="mb-4 flex flex-wrap justify-end gap-2">
                          {abierto && (
                            <button
                              type="button"
                              onClick={() => setCargandoExcel(true)}
                              className={BOTON_SECUNDARIO}
                            >
                              <FileSpreadsheet className="size-4" aria-hidden />
                              Cargar desde Excel
                            </button>
                          )}
                          {abierto && (
                            <button
                              type="button"
                              onClick={() => setCerrando(true)}
                              className={BOTON_PRINCIPAL}
                            >
                              <Lock className="size-4" aria-hidden />
                              Cerrar ciclo
                            </button>
                          )}
                          {ciclo.estado === 'CERRADO' && (
                            <button
                              type="button"
                              onClick={() => reabrir.mutate()}
                              disabled={reabrir.isPending || hayAbierto}
                              title={hayAbierto ? 'Ya hay otro ciclo abierto' : undefined}
                              className={BOTON_SECUNDARIO}
                            >
                              <LockOpen className="size-4" aria-hidden />
                              Reabrir
                            </button>
                          )}
                        </div>
                      )}

                      <Cifras ciclo={ciclo} consolidacion={consolidado.data} />

                      <Pestanas
                        pestanas={[
                          {
                            clave: 'pesajes',
                            rotulo: 'Pesajes',
                            descripcion: 'Lo registrado',
                            icono: Scale,
                          },
                          {
                            clave: 'consolidado',
                            rotulo: 'Consolidado',
                            descripcion: 'Lo que se va a facturar',
                            icono: ListChecks,
                          },
                        ]}
                        activa={vista}
                        alElegir={setVista}
                        etiqueta="Qué ver"
                      />

                      {facturado && ciclo.sinFacturar > 0 && (
                        <div
                          role="status"
                          className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4"
                        >
                          <Undo2 className="mt-0.5 size-5 shrink-0 text-amber-600" aria-hidden />
                          <p className="text-xs uppercase leading-relaxed text-amber-900">
                            <strong>
                              {ciclo.sinFacturar === 1
                                ? '1 pesaje liberado'
                                : `${ciclo.sinFacturar} pesajes liberados`}
                            </strong>{' '}
                            por una nota de crédito o una anulación. Corríjalos o bórrelos aquí, y
                            luego refacture el ciclo en Facturación de generadores: solo se factura
                            lo liberado.
                          </p>
                        </div>
                      )}

                      {vista === 'consolidado' ? (
                        <TablaConsolidada consolidacion={consolidado.data} />
                      ) : (
                        <>
                          {abierto && puedeGestionar && (
                            <RegistroDePesaje ciclo={ciclo} alRegistrar={refrescar} />
                          )}
                          <Tabla
                            datos={pesajes.data}
                            cargando={pesajes.isPending}
                            columnas={columnas}
                            tamanio={tamanio}
                            alCambiarPagina={setPagina}
                            alCambiarTamanio={(n) => {
                              setTamanio(n);
                              setPagina(0);
                            }}
                            entidad="pesajes"
                            iconoVacio={Scale}
                            mensajeVacio="Ningún pesaje en este ciclo"
                            herramientas={
                              <CampoDeBusqueda
                                valor={texto}
                                alCambiar={(v) => {
                                  setTexto(v);
                                  setPagina(0);
                                }}
                                marcador="Generador, RUC o guía…"
                                className="w-72"
                              />
                            }
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <Paginador
              pagina={paginaDeMeses}
              porPagina={MESES_POR_PAGINA}
              total={todos.length}
              quePagina="meses"
              alCambiar={setPaginaDeMeses}
            />
          </div>
        </>
      )}

      {abriendo && (
        <ModalDeApertura
          existentes={todos}
          alCerrar={() => setAbriendo(false)}
          alAbrir={(c) => {
            setAbriendo(false);
            setExpandido(c.id);
            setPaginaDeMeses(0);
            refrescar();
          }}
        />
      )}

      {cargandoExcel && ciclo && (
        <CargaDePesajes
          ciclo={ciclo}
          alCerrar={() => setCargandoExcel(false)}
          alRegistrar={() => {
            setCargandoExcel(false);
            refrescar();
          }}
        />
      )}

      {corrigiendo && ciclo && (
        <ModalDeCorreccion
          ciclo={ciclo}
          pesaje={corrigiendo}
          alCerrar={() => setCorrigiendo(null)}
          alCorregir={() => {
            setCorrigiendo(null);
            refrescar();
          }}
        />
      )}

      {cerrando && ciclo && (
        <ModalDeCierre
          ciclo={ciclo}
          consolidacion={consolidado.data}
          alCerrar={() => setCerrando(false)}
          alConfirmar={() => {
            setCerrando(false);
            refrescar();
          }}
        />
      )}
    </>
  );
}

function Cifras({
  ciclo,
  consolidacion,
}: {
  ciclo: Ciclo;
  consolidacion: Consolidacion | undefined;
}) {
  const t = consolidacion?.totales;
  return (
    <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
      <Cifra
        valor={enMes(ciclo.periodo)}
        rotulo={`Ciclo ${ciclo.estado.toLowerCase()}`}
        nota={`SBU congelado $${ciclo.sbu.toFixed(2)}`}
      />
      <Cifra valor={t ? String(t.pesajes) : '…'} rotulo="Pesajes" nota="Registrados en el mes" />
      <Cifra
        valor={t ? String(t.generadores) : '…'}
        rotulo="Generadores"
        nota={consolidacion ? `${consolidacion.activosSinPesaje.length} activos sin pesaje` : ''}
      />
      <Cifra
        valor={t ? `${Math.round(t.kilos).toLocaleString('es-EC')} KG` : '…'}
        rotulo="Peso"
        nota={t ? `Recargo $${t.recargo.toFixed(2)}` : ''}
      />
      <Cifra
        valor={t ? `$${t.total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}` : '…'}
        rotulo="A facturar"
        nota="Servicio más recargo"
        destacada
      />
    </div>
  );
}

function Cifra({
  valor,
  rotulo,
  nota,
  destacada = false,
}: {
  valor: string;
  rotulo: string;
  nota: string;
  destacada?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${destacada ? 'border-marca-200 bg-marca-50' : 'border-armazon-200 bg-white'}`}
    >
      <span
        className={`importe block text-xl font-bold ${destacada ? 'text-marca-800' : 'text-armazon-800'}`}
      >
        {valor}
      </span>
      <span className="mt-1 block text-[10px] font-semibold uppercase text-armazon-600">
        {rotulo}
      </span>
      <span className="block text-[10px] uppercase text-armazon-400">{nota}</span>
    </div>
  );
}

function TablaConsolidada({ consolidacion }: { consolidacion: Consolidacion | undefined }) {
  const filas = consolidacion?.porGenerador ?? [];
  const columnas: Columna<ConsolidadoPorGenerador>[] = [
    {
      clave: 'generador',
      encabezado: 'Generador',
      contenido: (g) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-medium uppercase text-armazon-800">
            {g.nombre}
          </span>
          <span className="importe block text-[10px] text-armazon-400">{g.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'categorias',
      encabezado: 'Categoría',
      contenido: (g) => (
        <span className="text-[10px] uppercase text-armazon-600">{g.categorias}</span>
      ),
    },
    {
      clave: 'pesajes',
      encabezado: 'Pesajes',
      alinear: 'centro',
      contenido: (g) => <span className="importe text-xs">{g.pesajes}</span>,
    },
    {
      clave: 'kilos',
      encabezado: 'Peso',
      alinear: 'derecha',
      contenido: (g) => (
        <span className="importe text-xs">{g.kilos.toLocaleString('es-EC')} KG</span>
      ),
    },
    {
      clave: 'recargo',
      encabezado: 'Recargo',
      alinear: 'derecha',
      contenido: (g) => (
        <span className="importe text-xs text-amber-700">
          {g.recargo > 0 ? `$${g.recargo.toFixed(2)}` : '—'}
        </span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'A facturar',
      alinear: 'derecha',
      contenido: (g) => <span className="importe text-xs font-bold">${g.total.toFixed(2)}</span>,
    },
  ];

  return (
    <>
      <AvisoSinPesaje consolidacion={consolidacion} />
      <Tabla
        datos={{
          contenido: filas,
          totalElementos: filas.length,
          pagina: 0,
          tamanio: Math.max(filas.length, 1),
          totalPaginas: 1,
        }}
        cargando={consolidacion === undefined}
        columnas={columnas}
        tamanio={filas.length}
        alCambiarPagina={() => {}}
        alCambiarTamanio={() => {}}
        paginado={false}
        entidad="generadores"
        iconoVacio={ListChecks}
        mensajeVacio="Nada que facturar todavía"
      />
    </>
  );
}

function AvisoSinPesaje({ consolidacion }: { consolidacion: Consolidacion | undefined }) {
  const [viendo, setViendo] = useState(false);
  const [filtro, setFiltro] = useState('');
  const sin = consolidacion?.activosSinPesaje ?? [];
  if (sin.length === 0) return null;

  const buscado = filtro.trim().toUpperCase();
  const vistos = buscado
    ? sin.filter(
        (g) => g.nombre.toUpperCase().includes(buscado) || g.identificacion.includes(buscado),
      )
    : sin;

  return (
    <div className="mb-4 overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-4 bg-amber-50/70 px-5 py-4">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-700">
          <AlertTriangle className="size-5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold uppercase text-amber-900">
            <span className="importe">{sin.length}</span>{' '}
            {sin.length === 1 ? 'generador activo sin pesajes' : 'generadores activos sin pesajes'}
          </span>
          <span className="mt-0.5 block text-[11px] uppercase text-amber-800/70">
            Están activos pero no tienen ningún pesaje en este ciclo: no se les factura nada
          </span>
        </span>
        <button
          type="button"
          onClick={() => setViendo(!viendo)}
          aria-expanded={viendo}
          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase text-amber-800 shadow-sm transition-colors hover:bg-amber-100"
        >
          {viendo ? 'Ocultar' : 'Ver quiénes'}
          <ChevronRight
            className={`size-4 transition-transform ${viendo ? 'rotate-90' : ''}`}
            aria-hidden
          />
        </button>
      </div>

      {viendo && (
        <div className="border-t border-amber-200 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <CampoDeBusqueda
              valor={filtro}
              alCambiar={setFiltro}
              marcador="Nombre o RUC…"
              className="w-72"
            />
            <span className="text-[11px] uppercase text-armazon-400">
              {vistos.length === sin.length
                ? `${sin.length} generadores`
                : `${vistos.length} de ${sin.length} generadores`}
            </span>
          </div>
          {vistos.length === 0 ? (
            <p className="py-6 text-center text-xs uppercase text-armazon-400">
              Ninguno coincide con la búsqueda
            </p>
          ) : (
            <ul className="grid max-h-80 grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2 xl:grid-cols-3">
              {vistos.map((g) => (
                <li
                  key={g.generadorId}
                  className="flex min-w-0 items-center gap-3 rounded-xl border border-armazon-100 bg-armazon-50/60 px-3 py-2"
                >
                  <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-white text-[10px] font-bold text-armazon-500 shadow-sm">
                    {iniciales(g.nombre)}
                  </span>
                  <span className="min-w-0">
                    <span
                      title={g.nombre}
                      className="block truncate text-xs font-semibold uppercase text-armazon-800"
                    >
                      {g.nombre}
                    </span>
                    <span className="importe block text-[10px] text-armazon-400">
                      {g.identificacion}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function iniciales(nombre: string): string {
  return nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((palabra) => palabra[0])
    .join('');
}

function ModalDeApertura({
  existentes,
  alCerrar,
  alAbrir,
}: {
  existentes: Ciclo[];
  alCerrar: () => void;
  alAbrir: (c: Ciclo) => void;
}) {
  const sugerido = mesAnterior();
  const [periodo, setPeriodo] = useState<string | null>(
    existentes.some((c) => c.periodo === sugerido) ? null : sugerido,
  );
  const abrir = useMutation({
    mutationFn: () => api.post<Ciclo>('/generadores/ciclos', { periodo }),
    onSuccess: (c) => {
      notificar.exito(`${enMes(c.periodo)} · SBU $${c.sbu.toFixed(2)}`, 'Ciclo abierto');
      alAbrir(c);
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO ABRIR'),
  });
  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Abrir ciclo"
      subtitulo="El mes de pesajes que se va a facturar"
      icono={CalendarPlus}
      ancho="estrecho"
    >
      <div className="grid gap-4">
        <SelectorDeMes elegido={periodo} existentes={existentes} alElegir={setPeriodo} />
        <p className="rounded-lg bg-armazon-50 px-3 py-2 text-[11px] uppercase leading-relaxed text-armazon-500">
          Se abre a <strong className="text-armazon-700">mes caído</strong>: en noviembre, el de
          octubre. Se congela el SBU de hoy y todo el mes se cobra con él. Solo puede haber un ciclo
          abierto a la vez.
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={alCerrar} className={BOTON_SECUNDARIO}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => abrir.mutate()}
            disabled={!periodo || abrir.isPending}
            className={BOTON_PRINCIPAL}
          >
            {periodo ? `Abrir ${enMesLargo(periodo)}` : 'Elija el mes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Los doce meses de un año, como una cuadrícula. Solo se elige un mes YA TERMINADO que no
 * tenga ciclo: el en curso y los futuros salen apagados, y los que ya tienen ciclo dicen cómo
 * están. Es la misma regla que aplica el servidor.
 */
function SelectorDeMes({
  elegido,
  existentes,
  alElegir,
}: {
  elegido: string | null;
  existentes: Ciclo[];
  alElegir: (periodo: string) => void;
}) {
  const ultimo = mesAnterior();
  const anioMaximo = Number(ultimo.slice(0, 4));
  const [anio, setAnio] = useState(Number((elegido ?? ultimo).slice(0, 4)));
  const porPeriodo = new Map(existentes.map((c) => [c.periodo, c]));

  return (
    <div className="overflow-hidden rounded-xl border border-armazon-200">
      <div className="flex items-center justify-between border-b border-armazon-100 bg-armazon-50 px-2 py-1.5">
        <button
          type="button"
          onClick={() => setAnio(anio - 1)}
          disabled={anio <= ANIO_MINIMO}
          aria-label="Año anterior"
          className="grid size-8 place-items-center rounded-lg text-armazon-500 transition-colors hover:bg-white disabled:opacity-30"
        >
          <ChevronLeft className="size-4" aria-hidden />
        </button>
        <span className="importe text-sm font-bold text-armazon-800">{anio}</span>
        <button
          type="button"
          onClick={() => setAnio(anio + 1)}
          disabled={anio >= anioMaximo}
          aria-label="Año siguiente"
          className="grid size-8 place-items-center rounded-lg text-armazon-500 transition-colors hover:bg-white disabled:opacity-30"
        >
          <ChevronRight className="size-4" aria-hidden />
        </button>
      </div>

      <div className="grid grid-cols-4 gap-1.5 p-2.5">
        {MESES.map((nombre, i) => {
          const periodo = `${anio}-${String(i + 1).padStart(2, '0')}`;
          const ciclo = porPeriodo.get(periodo);
          const sinTerminar = periodo > ultimo;
          const activo = periodo === elegido;
          const motivo = sinTerminar
            ? 'Este mes aún no ha terminado'
            : ciclo
              ? `Ya tiene ciclo (${ciclo.estado.toLowerCase()})`
              : undefined;
          return (
            <button
              key={periodo}
              type="button"
              onClick={() => alElegir(periodo)}
              disabled={sinTerminar || ciclo !== undefined}
              title={motivo}
              aria-pressed={activo}
              className={`relative flex h-12 flex-col items-center justify-center rounded-lg border text-xs font-semibold uppercase transition-colors ${
                activo
                  ? 'border-marca-600 bg-marca-600 text-white shadow-sm'
                  : ciclo
                    ? 'cursor-not-allowed border-armazon-100 bg-armazon-50 text-armazon-400'
                    : sinTerminar
                      ? 'cursor-not-allowed border-transparent text-armazon-300'
                      : 'border-armazon-200 bg-white text-armazon-700 hover:border-marca-400 hover:bg-marca-50'
              }`}
            >
              {nombre}
              {ciclo && (
                <span
                  className={`mt-0.5 text-[8px] font-bold tracking-wide ${
                    ciclo.estado === 'ABIERTO' ? 'text-sky-600' : 'text-marca-600'
                  }`}
                >
                  {ciclo.estado === 'FACTURADO'
                    ? 'Facturado'
                    : ciclo.estado === 'CERRADO'
                      ? 'Cerrado'
                      : 'Abierto'}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Hasta dónde se puede retroceder al abrir un ciclo atrasado. */
const ANIO_MINIMO = 2025;

function ModalDeCierre({
  ciclo,
  consolidacion,
  alCerrar,
  alConfirmar,
}: {
  ciclo: Ciclo;
  consolidacion: Consolidacion | undefined;
  alCerrar: () => void;
  alConfirmar: () => void;
}) {
  const cerrar = useMutation({
    mutationFn: () => api.post<Ciclo>(`/generadores/ciclos/${ciclo.id}/cerrar`),
    onSuccess: (c) => {
      notificar.exito(enMes(c.periodo), 'Ciclo cerrado');
      alConfirmar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO CERRAR'),
  });
  const t = consolidacion?.totales;
  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo={`Cerrar ${enMes(ciclo.periodo)}`}
      subtitulo="Ya no entrarán más pesajes"
      icono={Lock}
      ancho="medio"
    >
      <div className="grid gap-4">
        {t && (
          <div className="grid grid-cols-3 gap-3 text-center">
            <Cifra valor={String(t.generadores)} rotulo="Generadores" nota="Con factura" />
            <Cifra valor={String(t.pesajes)} rotulo="Pesajes" nota="" />
            <Cifra valor={`$${t.total.toFixed(2)}`} rotulo="A facturar" nota="" destacada />
          </div>
        )}
        <AvisoSinPesaje consolidacion={consolidacion} />
        <p className="text-[11px] uppercase leading-relaxed text-armazon-500">
          Cerrado, el ciclo queda listo para facturar. Si falta un pesaje se puede reabrir mientras
          no se haya facturado.
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={alCerrar} className={BOTON_SECUNDARIO}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => cerrar.mutate()}
            disabled={cerrar.isPending || !t || t.pesajes === 0}
            className={BOTON_PRINCIPAL}
          >
            <Lock className="size-4" aria-hidden />
            Cerrar ciclo
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Corrige un pesaje sin factura: se vuelve a tasar con el SBU de su ciclo. */
function ModalDeCorreccion({
  ciclo,
  pesaje,
  alCerrar,
  alCorregir,
}: {
  ciclo: Ciclo;
  pesaje: Pesaje;
  alCerrar: () => void;
  alCorregir: () => void;
}) {
  const [fecha, setFecha] = useState(pesaje.fecha);
  const [kilos, setKilos] = useState(String(pesaje.kilos));
  const [km, setKm] = useState(pesaje.kmExcedentes > 0 ? String(pesaje.kmExcedentes) : '');
  const [guia, setGuia] = useState(pesaje.guia ?? '');
  const [observacion, setObservacion] = useState(pesaje.observacion ?? '');
  const peso = Number(kilos.replace(',', '.'));
  const kmEscritos = km.trim() === '' ? null : Number(km.replace(',', '.'));

  const corregir = useMutation({
    mutationFn: () =>
      api.put<Pesaje>(`/generadores/pesajes/${pesaje.id}`, {
        generadorId: pesaje.generadorId,
        categoriaId: pesaje.categoriaId,
        fecha,
        kilos: peso,
        kmExcedentes: kmEscritos !== null && Number.isFinite(kmEscritos) ? kmEscritos : null,
        guia,
        observacion,
      }),
    meta: { procesando: 'Volviendo a tasar el pesaje…' },
    onSuccess: (p) => {
      notificar.exito(`$${p.total.toFixed(2)}`, 'Pesaje corregido');
      alCorregir();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO CORREGIR'),
  });

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Corregir pesaje"
      subtitulo={`${pesaje.generador} · ${enMes(ciclo.periodo)}`}
      icono={Pencil}
      ancho="medio"
    >
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-3">
          <Campo rotulo="Fecha">
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="h-10 rounded-lg border border-armazon-200 px-3 text-sm"
            />
          </Campo>
          <Campo rotulo={`Peso (kg) · antes ${pesaje.kilos.toLocaleString('es-EC')}`}>
            <input
              inputMode="decimal"
              value={kilos}
              onChange={(e) => setKilos(e.target.value)}
              className="importe h-10 rounded-lg border border-armazon-200 px-3 text-sm"
            />
          </Campo>
          <Campo rotulo="Km en exceso">
            <input
              inputMode="decimal"
              value={km}
              onChange={(e) => setKm(e.target.value)}
              placeholder="Los de su ficha"
              className="importe h-10 rounded-lg border border-armazon-200 px-3 text-sm"
            />
          </Campo>
          <Campo rotulo="Guía">
            <input
              value={guia}
              onChange={(e) => setGuia(e.target.value.toUpperCase())}
              className="importe h-10 rounded-lg border border-armazon-200 px-3 text-sm uppercase"
            />
          </Campo>
        </div>
        <Campo rotulo="Observación">
          <input
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
            className="h-10 rounded-lg border border-armazon-200 px-3 text-sm"
          />
        </Campo>
        <p className="text-[11px] uppercase leading-relaxed text-armazon-500">
          Se vuelve a tasar con el SBU del ciclo (${ciclo.sbu.toFixed(2)}). Antes costaba $
          {pesaje.total.toFixed(2)}.
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={alCerrar} className={BOTON_SECUNDARIO}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => corregir.mutate()}
            disabled={corregir.isPending || !fecha || !(peso > 0)}
            className={BOTON_PRINCIPAL}
          >
            <Pencil className="size-4" aria-hidden />
            Corregir
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Campo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[10px] font-semibold uppercase text-armazon-600">{rotulo}</span>
      {children}
    </label>
  );
}

const MESES_POR_PAGINA = 6;

const ESTILO_DEL_ESTADO: Record<Ciclo['estado'], { ficha: string; barra: string }> = {
  ABIERTO: { ficha: 'border-sky-200 bg-sky-50 text-sky-700', barra: 'bg-sky-400' },
  CERRADO: { ficha: 'border-amber-200 bg-amber-50 text-amber-700', barra: 'bg-amber-400' },
  FACTURADO: { ficha: 'border-marca-200 bg-marca-50 text-marca-700', barra: 'bg-marca-500' },
};

function Resumen({
  rotulo,
  valor,
  destacado = false,
}: {
  rotulo: string;
  valor: string;
  destacado?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border px-5 py-3.5 shadow-sm ${destacado ? 'border-marca-200 bg-marca-50' : 'border-armazon-200 bg-white'}`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">{rotulo}</p>
      <p
        className={`importe mt-1 text-xl font-bold ${destacado ? 'text-marca-800' : 'text-armazon-900'}`}
      >
        {valor}
      </p>
    </div>
  );
}

function enDolares(n: number): string {
  return n.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Lo ultimo que le paso al ciclo, con su fecha. */
function enQueQuedo(c: Ciclo): string {
  if (c.estado === 'FACTURADO' && c.facturadoEn) {
    return `Facturado el ${enFecha(c.facturadoEn.slice(0, 10))}`;
  }
  if (c.estado === 'CERRADO' && c.cerradoEn) {
    return `Cerrado el ${enFecha(c.cerradoEn.slice(0, 10))}`;
  }
  return `Abierto el ${enFecha(c.abiertoEn.slice(0, 10))}`;
}

function EstadoDelCiclo({ estado }: { estado: Ciclo['estado'] }) {
  const estilo =
    estado === 'ABIERTO'
      ? 'bg-sky-50 text-sky-700 border-sky-200'
      : estado === 'CERRADO'
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-marca-50 text-marca-700 border-marca-200';
  return (
    <span
      className={`inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${estilo}`}
    >
      {estado === 'FACTURADO' ? 'Facturado' : estado === 'CERRADO' ? 'Cerrado' : 'Abierto'}
    </span>
  );
}

const MESES_LARGOS = [
  'ENERO',
  'FEBRERO',
  'MARZO',
  'ABRIL',
  'MAYO',
  'JUNIO',
  'JULIO',
  'AGOSTO',
  'SEPTIEMBRE',
  'OCTUBRE',
  'NOVIEMBRE',
  'DICIEMBRE',
];

function enMesLargo(periodo: string): string {
  const [anio, mes] = periodo.split('-');
  return `${MESES_LARGOS[Number(mes) - 1] ?? mes} ${anio}`;
}

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

function enMes(periodo: string): string {
  const [anio, mes] = periodo.split('-');
  return `${MESES[Number(mes) - 1] ?? mes} ${anio}`;
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}

function mesAnterior(): string {
  const hoy = new Date();
  const d = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const BOTON_PRINCIPAL =
  'inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm transition-colors hover:bg-marca-700 disabled:opacity-50';
const BOTON_SECUNDARIO =
  'inline-flex h-10 items-center gap-2 rounded-lg border border-armazon-300 bg-white px-4 text-xs font-bold uppercase text-armazon-700 shadow-sm transition-colors hover:bg-armazon-50 disabled:opacity-50';
